import { useMemo } from 'react';
import chroma from 'chroma-js';
import { Layer, LayerProps } from 'shared/map-exports';
import { FeatureCollection } from 'geojson';
import { MapLayerProps } from 'features/mapping/mapping.types';
import { addVisible } from 'shared/addVisible';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { ArcGisLayer } from '../ArcGisLayer';

const fields: string[] = ['acq_time', 'hours_old', 'confidence'];

const colorStops0to24 = (() => {
  const scale = chroma
    .scale(['red', 'orange'])
    .domain([0, 24])
    .mode('lab')
    .correctLightness();
  const stops: (number | string)[] = [];
  for (let i = 0; i <= 24; i += 1) stops.push(i, scale(i).hex());
  return stops;
})();

const baseStyle: LayerProps = {
  id: 'viirs',
  type: 'circle',
  filter: [
    'all',
    ['has', 'hours_old'],
    ['>=', ['get', 'hours_old'], 0],
    ['<=', ['get', 'hours_old'], 24],
  ],
  paint: {
    'circle-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      3,
      0.35,
      8,
      0.5,
      12,
      0.5,
      15,
      0.2,
      18,
      0.05,
    ],
    'circle-blur': 0.5,
    'circle-radius': [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      0,
      2,
      22,
      20000,
    ],
    'circle-color': [
      'interpolate',
      ['linear'],
      ['min', 24, ['max', 0, ['to-number', ['get', 'hours_old']]]],
      ...colorStops0to24,
    ],
  },
};

// service already provides hours_old, so pass-through
const processData = (fc: FeatureCollection): FeatureCollection => fc;

export const ViirsLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;

  const style = useMemo(() => addVisible(baseStyle, visible), [visible]);

  return (
    <ArcGisLayer
      visible={visible}
      src="https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Satellite_VIIRS_Thermal_Hotspots_and_Fire_Activity/FeatureServer/0"
      where={`hours_old < 24 AND confidence IN ('nominal', 'high')`}
      orderBy="acq_time DESC"
      fields={fields}
      precision={4}
      onData={processData}
      staleTime={60 * 60 * 1000} // 60 minutes
    >
      <Layer beforeId={OVERLAY_ICONS_BEFORE_ID} {...style} />
    </ArcGisLayer>
  );
};
